import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ownerIds } from 'src/components/form';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import type { useGetImpactById } from 'src/hooks/queries/impact/useGetImpactById';
import { getOwners } from 'src/rbac/contributorHelper';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useUpdateImpact } from '@/hooks/mutations/impact/useUpdateImpact';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';

import ImpactForm from '../../forms/ImpactForm';
import type { ImpactFormFieldData } from '../../forms/impactFormSchema';
import { defaultValues } from '../../forms/impactFormSchema';

type GetImpactByIdRow = NonNullable<
  ReturnType<typeof useGetImpactById>['data']
>['impact'][number];

type Props = {
  impact: GetImpactByIdRow;
};

const Tab: FC<Props> = ({ impact }) => {
  useI18NSummaryHelpContent('impacts.detailsHelp');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const formPrefillEnabled = useIsFormPrefillEnabled();
  const { hasPermission: canUpdateImpact, loading: canUpdateImpactLoading } =
    useHasPermissionQuery('update:impact', impact);
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const { updateImpact } = useUpdateImpact();

  const onSave = async (data: ImpactFormFieldData) => {
    if (!impact) {
      throw new Error('Missing impact data');
    }
    await updateImpact({
      Id: impact.Id,
      // Optimistic-concurrency token: the snapshot this form was loaded from.
      // A second editor saving over a newer row is rejected with a 409 rather
      // than silently winning (COI-622).
      OriginalTimestamp: impact.ModifiedAtTimestamp,
      RatingGuidance: data.RatingGuidance,
      Rationale: data.Rationale,
      Name: data.Name,
      LikelihoodAppetite: data.LikelihoodAppetite,
      ...ownerIds(data),
    });
  };

  const onDismiss = () => navigate(-1);

  return (
    <ImpactForm
      onDismiss={onDismiss}
      header={t('details')}
      readOnly={!canUpdateImpact || canUpdateImpactLoading}
      onSave={onSave}
      publishToolbarState={objectToolbarEnabled}
      publishActiveForm={formPrefillEnabled}
      activeFormRecordId={impact.Id}
      values={{
        ...defaultValues,
        ...impact,
        Owners: getOwners(impact),
      }}
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
    />
  );
};

export default Tab;
